import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { vinSessionService } from '@web/services/singletons';
import type { CvtConcernDoc } from '@web/vin-sessions/types';
import { openDocumentTab } from '@web/vin-sessions/utils/open-document-tab';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { CvtDiagnosticNotRequired } from './cvt-diagnostic-not-required';
import {
    CvtConcern,
    type CvtConcernOption,
    type CvtPrimaryConcernsModalContent,
} from './cvt-primary-concerns-modal.types';
import { toCvtPrimaryConcernsModalContent } from './cvt-primary-concerns-modal.utils';
import { RunCvtDiagnostic } from './run-cvt-diagnostic';
import { SelectConcerns } from './select-concerns';

const logger = getLogger('cvt');

export type CvtPrimaryConcernsModalProps = {
    /** Whether the file is being uploaded and run. */
    isUploading: boolean;

    /** Closes the modal. */
    onClose: () => void;

    /** Navigates the user to the STIS page. */
    onGoToStis: () => void;

    /** Uploads the file and runs the diagnostic. Rejects with why it failed. */
    onRunDiagnostic: (file: File, repairOrderNumber: string) => Promise<void>;

    /** Whether the modal is open. */
    show: boolean;

    /** Takes the user back to where they opened the modal from. Without it, the concerns show no Back button. */
    onBack?: () => void;
};

/**
 * The modal for running a CVT diagnostic.
 *
 * Renders one content at a time with the `selectConcerns` first, then the next content based on the selected concerns.
 */
export function CvtPrimaryConcernsModal({
    isUploading,
    onClose,
    onGoToStis,
    onRunDiagnostic,
    show,
    onBack,
}: CvtPrimaryConcernsModalProps) {
    const { t } = useTranslation();
    const { abortSignalRef, signalAbort } = useAbortController();

    const [content, setContent] = useState<CvtPrimaryConcernsModalContent>('selectConcerns');
    const [selectedConcernOptions, setSelectedConcernOptions] = useState<CvtConcernOption[]>([]);

    const [cvtConcernDocs, setCvtConcernDocs] = useState<CvtConcernDoc[]>([]);
    const [hasFailedToLoadCvtConcernDocs, setHasFailedToLoadCvtConcernDocs] = useState<boolean>(false);
    const [isLoadingCvtConcernDocs, setIsLoadingCvtConcernDocs] = useState<boolean>(true);

    const [showPopupBlockedAlert, setShowPopupBlockedAlert] = useState<boolean>(false);

    const concernOptions: CvtConcernOption[] = [
        { title: t('cvt:selectConcernsModal.concerns.dtc'), value: CvtConcern.Dtc },
        { title: t('cvt:selectConcernsModal.concerns.noiseOrLeak'), value: CvtConcern.NoiseOrLeak },
        { title: t('cvt:selectConcernsModal.concerns.drivability'), value: CvtConcern.Drivability },
    ];

    function handleBack() {
        setContent('selectConcerns');
        setSelectedConcernOptions([]);
        setShowPopupBlockedAlert(false);
    }

    function handleDocumentOpen(preSignedUrl: string) {
        openDocumentTab(async () => preSignedUrl, handlePopupBlocked);
    }

    function handleModalExited() {
        setContent('selectConcerns');
        setSelectedConcernOptions([]);
        setCvtConcernDocs([]);
        setHasFailedToLoadCvtConcernDocs(false);
        setIsLoadingCvtConcernDocs(true);
        setShowPopupBlockedAlert(false);
    }

    function handlePopupBlocked() {
        setShowPopupBlockedAlert(true);
    }

    function handlePopupBlockedAlertClose() {
        setShowPopupBlockedAlert(false);
    }

    function handleSelectConcernsContinue() {
        setContent(toCvtPrimaryConcernsModalContent(selectedConcernOptions));
    }

    function handleSelectedConcernOptionsChange(nextSelectedConcernOptions: CvtConcernOption[]) {
        setSelectedConcernOptions(nextSelectedConcernOptions);
    }

    useEffect(() => {
        signalAbort();

        if (!show) {
            return;
        }

        async function fetchCvtConcernDocs() {
            setCvtConcernDocs([]);
            setHasFailedToLoadCvtConcernDocs(false);
            setIsLoadingCvtConcernDocs(true);

            try {
                const response = await vinSessionService.getCvtConcernDocs({ signal: abortSignalRef.current });

                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }

                setCvtConcernDocs(response.data);
                setIsLoadingCvtConcernDocs(false);
            } catch (error) {
                setHasFailedToLoadCvtConcernDocs(true);
                setIsLoadingCvtConcernDocs(false);
                logger.error('Failed to fetch the CVT concern docs', { error });
            }
        }

        fetchCvtConcernDocs();
    }, [abortSignalRef, show, signalAbort]);

    return (
        <Modal
            backdrop={isUploading ? 'static' : true}
            centered
            keyboard={!isUploading}
            onExited={handleModalExited}
            onHide={onClose}
            show={show}
            size="lg"
        >
            {content === 'selectConcerns' && (
                <SelectConcerns
                    concernOptions={concernOptions}
                    onBack={onBack}
                    onContinue={handleSelectConcernsContinue}
                    onSelectedConcernOptionsChange={handleSelectedConcernOptionsChange}
                    selectedConcernOptions={selectedConcernOptions}
                />
            )}
            {content === 'cvtDiagnosticNotRequired' && (
                <CvtDiagnosticNotRequired
                    cvtConcernDocs={cvtConcernDocs}
                    hasFailedToLoadCvtConcernDocs={hasFailedToLoadCvtConcernDocs}
                    isLoadingCvtConcernDocs={isLoadingCvtConcernDocs}
                    onBack={handleBack}
                    onDocumentOpen={handleDocumentOpen}
                    onGoToStis={onGoToStis}
                    onPopupBlockedAlertClose={handlePopupBlockedAlertClose}
                    selectedConcernOptions={selectedConcernOptions}
                    showPopupBlockedAlert={showPopupBlockedAlert}
                />
            )}
            {content === 'runCvtDiagnostic' && (
                <RunCvtDiagnostic isUploading={isUploading} onBack={handleBack} onRunDiagnostic={onRunDiagnostic} />
            )}
        </Modal>
    );
}
